export interface MembershipStatusSnapshotShape {
  userId?: string | null;
  accountType?: string | null;
  subscription?: string | null;
  paymentStatus?: string | null;
  autoRenew?: boolean | null;
  billingCycle?: string | null;
  currentPlanTier?: string | null;
  nextRenewalDate?: string | null;
  subscriptionStartDate?: string | null;
  subscriptionEndDate?: string | null;
  monthlyPlatformFeeNextBillingDate?: string | null;
  latestTransactionId?: string | null;
}

const hasText = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const pickText = (
  primary?: string | null,
  fallback?: string | null
): string | null => {
  if (hasText(primary)) {
    return primary;
  }

  if (hasText(fallback)) {
    return fallback;
  }

  return primary ?? fallback ?? null;
};

const pickBoolean = (
  primary?: boolean | null,
  fallback?: boolean | null
): boolean | null => {
  if (typeof primary === "boolean") {
    return primary;
  }

  if (typeof fallback === "boolean") {
    return fallback;
  }

  return primary ?? fallback ?? null;
};

const parseDateValue = (value?: string | null): Date | null => {
  if (!hasText(value)) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getMembershipEndDate = <T extends MembershipStatusSnapshotShape>(
  snapshot?: T | null
): Date | null =>
  parseDateValue(snapshot?.subscriptionEndDate ?? snapshot?.nextRenewalDate ?? null);

export const mergeMembershipStatusSnapshots = <T extends MembershipStatusSnapshotShape>(
  primary?: T | null,
  fallback?: T | null
): T | null => {
  if (!primary && !fallback) {
    return null;
  }

  return {
    ...(fallback ?? {}),
    ...(primary ?? {}),
    accountType: pickText(primary?.accountType, fallback?.accountType),
    subscription: pickText(primary?.subscription, fallback?.subscription),
    paymentStatus: pickText(primary?.paymentStatus, fallback?.paymentStatus),
    autoRenew: pickBoolean(primary?.autoRenew, fallback?.autoRenew),
    billingCycle: pickText(primary?.billingCycle, fallback?.billingCycle),
    currentPlanTier: pickText(primary?.currentPlanTier, fallback?.currentPlanTier),
    nextRenewalDate: pickText(primary?.nextRenewalDate, fallback?.nextRenewalDate),
    subscriptionStartDate: pickText(
      primary?.subscriptionStartDate,
      fallback?.subscriptionStartDate
    ),
    subscriptionEndDate: pickText(
      primary?.subscriptionEndDate,
      fallback?.subscriptionEndDate
    ),
    monthlyPlatformFeeNextBillingDate: pickText(
      primary?.monthlyPlatformFeeNextBillingDate,
      fallback?.monthlyPlatformFeeNextBillingDate
    ),
    latestTransactionId: pickText(
      primary?.latestTransactionId,
      fallback?.latestTransactionId
    ),
  } as T;
};

export const resolveMembershipStatusAfterMutation = <
  T extends MembershipStatusSnapshotShape,
>({
  mutationSnapshot,
  refreshedSnapshot,
  expectedAutoRenew,
  referenceDate = new Date(),
}: {
  mutationSnapshot?: T | null;
  refreshedSnapshot?: T | null;
  expectedAutoRenew?: boolean;
  referenceDate?: Date;
}): T | null => {
  const refreshedFirst = mergeMembershipStatusSnapshots(
    refreshedSnapshot,
    mutationSnapshot
  );

  if (!refreshedFirst) {
    return null;
  }

  if (
    typeof expectedAutoRenew === "boolean" &&
    refreshedFirst.autoRenew !== expectedAutoRenew &&
    mutationSnapshot?.autoRenew === expectedAutoRenew
  ) {
    return mergeMembershipStatusSnapshots(mutationSnapshot, refreshedSnapshot);
  }

  const mutationEndDate = getMembershipEndDate(mutationSnapshot);
  const resolvedEndDate = getMembershipEndDate(refreshedFirst);
  const mutationHasFutureEndDate =
    !!mutationEndDate && mutationEndDate.getTime() > referenceDate.getTime();
  const resolvedHasFutureEndDate =
    !!resolvedEndDate && resolvedEndDate.getTime() > referenceDate.getTime();

  if (
    expectedAutoRenew === false &&
    mutationHasFutureEndDate &&
    !resolvedHasFutureEndDate
  ) {
    return mergeMembershipStatusSnapshots(mutationSnapshot, refreshedSnapshot);
  }

  return refreshedFirst;
};
